<template>
  <div class="view generating" :style="{ '--p-color': persona.color, '--p-glow': persona.glowColor }">
    <p class="gen__eyebrow orbitron">PASUL 3 / 3</p>

    <transition name="fade" mode="out-in">
      <div :key="stage" class="gen__stage-wrap">
        <h2 class="gen__title orbitron">{{ stageConfig[stage].title }}</h2>
        <p class="gen__sub">{{ stageConfig[stage].sub }}</p>
      </div>
    </transition>

    <!-- Visual loader -->
    <div class="gen__visual">
      <div class="gen__rings">
        <div class="gen__ring" v-for="n in 4" :key="n" :style="{ animationDelay: (n-1) * 0.3 + 's' }"></div>
      </div>
      <div class="gen__center">
        <img v-if="stage === 'video' && generatedPhoto" :src="generatedPhoto" class="gen__photo-preview" />
        <div v-else class="gen__loader-icon orbitron">AI</div>
      </div>
    </div>

    <!-- Progress -->
    <div class="gen__progress-wrap">
      <div class="gen__progress-track">
        <div class="gen__progress-fill" :style="{ width: displayProgress + '%' }"></div>
      </div>
      <p class="gen__status orbitron">{{ stageConfig[stage].status }}</p>
    </div>

    <div class="gen__error" v-if="error">
      <p>{{ error }}</p>
      <button class="neon-btn" @click="retry">ÎNCEARCĂ DIN NOU</button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useSessionStore } from '@/store/session'
import { PERSONAS, PERSONA_PROMPTS } from '@/data/personas'
import { generateImage, generateVideo } from '@/services/comfyui'

const router = useRouter()
const session = useSessionStore()

const persona = computed(() => PERSONAS[session.personaId] || PERSONAS.authentic)
const generatedPhoto = computed(() => session.generatedPhoto)

const stage = ref('photo')
const imageProgress = ref(0)
const videoProgress = ref(0)
const error = ref(null)

const displayProgress = computed(() =>
  stage.value === 'photo' ? Math.round(imageProgress.value * 100) : Math.round(videoProgress.value * 100)
)

const stageConfig = {
  photo: {
    title: 'GENERĂM POZA TA',
    sub: 'AI-ul creează imaginea ta personalizată în stilul alter ego-ului tău.',
    status: 'GENERATING IMAGE...',
  },
  video: {
    title: 'ANIMĂM POZA TA',
    sub: 'Transformăm imaginea ta într-un film cinematic de 5 secunde.',
    status: 'CREATING VIDEO...',
  },
}

async function run() {
  error.value = null
  try {
    const prompts = PERSONA_PROMPTS[session.personaId] || PERSONA_PROMPTS.authentic
    const imgPrompt = prompts.image(session.gender || 'male')

    stage.value = 'photo'
    const photoUrl = await generateImage(session.selfiePhoto, imgPrompt, (p) => { imageProgress.value = p })
    imageProgress.value = 1
    session.generatedPhoto = photoUrl

    stage.value = 'video'
    const videoUrl = await generateVideo(photoUrl, prompts.video, (p) => { videoProgress.value = p })
    videoProgress.value = 1
    session.generatedVideo = videoUrl

    router.push('/result')
  } catch (e) {
    error.value = e.message || 'Generarea a eșuat. Încearcă din nou.'
  }
}

function retry() {
  imageProgress.value = 0
  videoProgress.value = 0
  run()
}

onMounted(run)
</script>

<style scoped>
.generating {
  padding: 2.5rem 4rem;
  gap: 2rem;
  --p-color: #7c3aed;
  --p-glow: rgba(139, 92, 246, 0.6);
}

.gen__eyebrow {
  font-size: 0.8rem;
  letter-spacing: 0.4em;
  color: rgba(255, 255, 255, 0.3);
}

.gen__stage-wrap {
  text-align: center;
}

.gen__title {
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  letter-spacing: 0.08em;
  color: #fff;
  text-shadow: 0 0 30px var(--p-glow);
  margin-bottom: 0.7rem;
}

.gen__sub {
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.4);
  max-width: 600px;
  letter-spacing: 0.03em;
  line-height: 1.6;
}

.gen__visual {
  position: relative;
  width: 280px;
  height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.gen__rings {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gen__ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 1px solid var(--p-color);
  opacity: 0;
  animation: rippleOut 2.4s ease-out infinite;
}

@keyframes rippleOut {
  0% { transform: scale(0.4); opacity: 0.8; }
  100% { transform: scale(1.0); opacity: 0; }
}

.gen__center {
  position: relative;
  z-index: 2;
  width: 160px;
  height: 160px;
  border-radius: 50%;
  background: rgba(0,0,0,0.4);
  border: 2px solid var(--p-color);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 40px var(--p-glow);
}

.gen__photo-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: blur(2px) brightness(0.7);
  animation: photoReveal 1s ease forwards;
}

@keyframes photoReveal {
  to { filter: blur(0) brightness(1); }
}

.gen__loader-icon {
  font-size: 2.5rem;
  font-weight: 900;
  color: var(--p-color);
  text-shadow: 0 0 20px var(--p-glow);
  animation: aiPulse 1.5s ease-in-out infinite;
}

@keyframes aiPulse {
  0%, 100% { opacity: 0.5; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.1); }
}

.gen__progress-wrap {
  width: 100%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.8rem;
}

.gen__progress-track {
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 4px;
  overflow: hidden;
}

.gen__progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--p-color), #60a5fa);
  border-radius: 4px;
  transition: width 0.3s ease;
  box-shadow: 0 0 8px var(--p-glow);
}

.gen__status {
  font-size: 0.75rem;
  letter-spacing: 0.3em;
  color: rgba(255, 255, 255, 0.3);
}

.gen__error {
  position: absolute;
  inset: 0;
  background: rgba(5, 5, 16, 0.92);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  z-index: 10;
  color: rgba(255, 100, 100, 0.8);
  font-size: 1.1rem;
  text-align: center;
  padding: 2rem;
}
</style>
